import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useUser } from "@clerk/clerk-react";
import {
  Search,
  X,
  RotateCcw,
  Box,
  BarChart3,
  Settings,
  CreditCard,
  ShieldAlert,
  Moon,
  Sun,
  HelpCircle,
  Code,
  LogOut,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { useEntitlement } from "@/contexts/EntitlementContext";
import { useFlag } from "@/contexts/FlagsContext";
import { useOptimizedAuthContext } from "@/contexts/OptimizedAuthContext";
import { useIsSuperadmin } from "@/hooks/useIsSuperadmin";
import { useRouter } from "@/hooks/useRouter";
import { useTheme } from "@/hooks/useTheme";

/**
 * Mobile menu sheet — morphs out of MobileBottomNav's circular menu button.
 *
 * Carries everything the desktop NavigationRibbon offers that didn't earn a
 * dock slot: search, the flag/entitlement-gated destinations (Analytics,
 * Resume, Skills), Settings, Billing, Admin, and the UserPill's account
 * actions (profile, theme, docs, sign out). Always mounted; open/close is
 * purely visual (opacity + transform + clip-path) so first interaction has
 * zero mount cost. Row vocabulary mirrors NavigationRibbonItem so desktop
 * and mobile share the same visual language.
 */
export function MobileMenuSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const router = useRouter();
  const { user } = useUser();
  const { signOut } = useOptimizedAuthContext();
  const { plan, can, loading: planLoading } = useEntitlement();
  const { theme, toggleTheme } = useTheme();
  const showAnalytics = useFlag("analytics");
  const showResume = useFlag("resume");
  const showSkills = useFlag("skills");
  const showBilling = useFlag("billing");
  const isSuperadmin = useIsSuperadmin();

  const name = user?.fullName ?? user?.emailAddresses?.[0]?.emailAddress ?? "Account";
  const email = user?.emailAddresses?.[0]?.emailAddress ?? "";
  const initial = name[0]?.toUpperCase() || "U";
  const planLabel = plan.charAt(0).toUpperCase() + plan.slice(1);
  // Entitlement defaults to free while loading — gate the upsell on !loading
  // so paid users don't see it flash during the fetch.
  const isFree = !planLoading && plan === "free";

  const handleNavigate = useCallback(
    (href: string) => {
      onClose();
      router.push(href);
    },
    [onClose, router],
  );

  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const handleSearchSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const trimmed = searchQuery.trim();
      if (!trimmed) return;
      handleNavigate(`/search?q=${encodeURIComponent(trimmed)}`);
      setSearchQuery("");
    },
    [searchQuery, handleNavigate],
  );

  // Focus management: capture the previously-focused element (the menu
  // button), move focus into the sheet, trap Tab within it, close on Esc,
  // and restore focus on close. Without this, keyboard and screen-reader
  // users would tab past the visible sheet into the hidden page behind it.
  const sheetContentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusableSelector =
      'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const raf = requestAnimationFrame(() => {
      sheetContentRef.current?.querySelector<HTMLElement>(focusableSelector)?.focus();
    });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const root = sheetContentRef.current;
      if (!root) return;
      const focusables = Array.from(root.querySelectorAll<HTMLElement>(focusableSelector)).filter(
        (el) => el.offsetParent !== null,
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (!root.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("keydown", onKey);
      previouslyFocused?.focus?.();
    };
  }, [open, onClose]);

  // Sheet floats 80px above the safe area — clear of the dock pill below it.
  const sheetBottom = "calc(env(safe-area-inset-bottom, 0px) + 80px)";

  return (
    <>
      <div
        id="mobile-menu-sheet"
        role="dialog"
        aria-modal={open}
        aria-hidden={!open}
        aria-label="Menu"
        // `visibility: hidden` cascades to descendants and prevents Safari
        // hit-tests from leaking through to the backdrop and rows when the
        // sheet is closed. The visibility flip is delayed past the close
        // motion (520ms) so the sheet doesn't snap away mid-animation.
        className="absolute inset-0 z-40"
        style={{
          visibility: open ? "visible" : "hidden",
          pointerEvents: open ? "auto" : "none",
          transition: open ? "visibility 0s linear 0s" : "visibility 0s linear 520ms",
        }}
      >
        {/* Transparent backdrop — captures click-outside without dimming. */}
        <button
          type="button"
          aria-label="Close menu"
          onClick={onClose}
          tabIndex={open ? 0 : -1}
          className="absolute inset-0 cursor-pointer"
        />

        <div
          className="absolute inset-x-0 px-3 flex justify-end pointer-events-none"
          style={{ bottom: sheetBottom }}
        >
          <div
            ref={sheetContentRef}
            // Sheet motion — a clip-path circle() anchored just below-right
            // of the sheet (at the menu button) expands from 0 to enclose the
            // sheet, so it reads as blooming out of the button; a subtle
            // rise + grow and an opacity ride reinforce it. Origin math:
            // the h-14 menu button sits ~28px past the sheet's right edge
            // (x = 100% + 24px) and ~44px below its bottom (y = 100% + 44px).
            className="mobile-menu-sheet pointer-events-auto w-full max-w-[22rem] flex flex-col bg-card border border-border rounded-xl shadow-xl overflow-hidden"
            style={{
              maxHeight: "82dvh",
              // `manipulation` removes the legacy 300ms double-tap-to-zoom
              // delay — the sheet is always single-tap UI.
              touchAction: "manipulation",
              willChange: "clip-path, transform, opacity",
              opacity: open ? 1 : 0,
              transform: open ? "translateY(0) scale(1)" : "translateY(8px) scale(0.96)",
              clipPath: open
                ? "circle(200% at calc(100% + 24px) calc(100% + 44px))"
                : "circle(0% at calc(100% + 24px) calc(100% + 44px))",
              transition: open
                ? "clip-path 520ms cubic-bezier(0.22, 1, 0.36, 1), transform 420ms cubic-bezier(0.22, 1, 0.36, 1), opacity 260ms ease-out"
                : "clip-path 360ms cubic-bezier(0.4, 0, 1, 1), transform 280ms cubic-bezier(0.4, 0, 1, 1), opacity 220ms ease-in",
            }}
          >
            {/* Identity header — the UserPill's avatar + name + plan. */}
            <div className="flex-shrink-0 flex items-center gap-2.5 p-3 border-b border-border">
              <span className="flex-shrink-0 h-8 w-8 rounded-full bg-brand-strong flex items-center justify-center overflow-hidden">
                {user?.imageUrl ? (
                  <img src={user.imageUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-brand-foreground text-xs font-semibold">{initial}</span>
                )}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block truncate text-sm font-medium text-foreground">{name}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {email ? `${email} · ` : ""}
                  {planLabel} plan
                </span>
              </span>
              {isFree && (
                <button
                  type="button"
                  // Straight to checkout — same one-click policy as the
                  // UserPill's Upgrade item; `src` rides into the /checkout
                  // pageview for funnel attribution.
                  onClick={() =>
                    handleNavigate(
                      `/checkout?plan=pro&next=${encodeURIComponent(window.location.pathname)}&src=mobile-menu`,
                    )
                  }
                  className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-brand/10 px-2.5 py-1.5 text-xs font-semibold text-brand ring-1 ring-inset ring-brand/20 active:bg-brand/20"
                >
                  <Sparkles size={12} /> Upgrade
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-2 min-h-0">
              {/* Search — replaces the ribbon search box on mobile. */}
              <form onSubmit={handleSearchSubmit} className="mb-2">
                <div className="relative">
                  <Search
                    size={14}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                  />
                  <input
                    ref={searchInputRef}
                    // `type="text"` (not `search`) — iOS Safari adds its own
                    // native clear button on search inputs, colliding with
                    // the custom X below.
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search…"
                    className="w-full h-10 pl-8 pr-7 text-sm bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-brand transition-colors"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      aria-label="Clear search"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              </form>

              {/* Destinations that didn't earn a dock slot. */}
              <nav aria-label="More destinations" className="space-y-0.5">
                {showAnalytics && (
                  <SheetRow
                    icon={BarChart3}
                    label="Analytics"
                    pro={!can("analytics")}
                    onClick={() => handleNavigate("/analytics")}
                  />
                )}
                {showResume && (
                  <SheetRow
                    icon={RotateCcw}
                    label="Resume"
                    pro={!can("resume")}
                    onClick={() => handleNavigate("/resume")}
                  />
                )}
                {showSkills && (
                  <SheetRow
                    icon={Box}
                    label="Skills"
                    pro={!can("skills")}
                    onClick={() => handleNavigate("/skills")}
                  />
                )}
                <SheetRow icon={Settings} label="Settings" onClick={() => handleNavigate("/settings")} />
                {showBilling && (
                  <SheetRow icon={CreditCard} label="Billing" onClick={() => handleNavigate("/billing")} />
                )}
                {isSuperadmin && (
                  <SheetRow
                    icon={ShieldAlert}
                    label="Admin"
                    onClick={() => handleNavigate("/admin/skills")}
                  />
                )}
              </nav>

              <div className="my-2 mx-2 border-t border-border" />

              {/* Account actions — mirrors the UserPill dropdown. No "Manage
                  profile" row here: profile lives on Settings' Account tab and
                  this menu already has a Settings row. */}
              <nav aria-label="Account" className="space-y-0.5">
                <SheetRow
                  icon={theme === "dark" ? Sun : Moon}
                  label={theme === "dark" ? "Light mode" : "Dark mode"}
                  onClick={toggleTheme}
                />
                <SheetRow
                  icon={HelpCircle}
                  label="Help & docs"
                  href="https://docs.specstory.com/cloud/overview"
                />
                <SheetRow
                  icon={Code}
                  label="API reference"
                  href="https://docs.specstory.com/api-reference/introduction"
                />
                <SheetRow
                  icon={LogOut}
                  label="Log out"
                  variant="danger"
                  onClick={() => {
                    onClose();
                    signOut();
                    router.push("/");
                  }}
                />
              </nav>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Menu sheet row — the NavigationRibbonItem vocabulary (18px icon, text-sm
 * font-medium, brand-tinted active/hover) at a touch-friendly height.
 */
function SheetRow({
  icon: Icon,
  label,
  onClick,
  href,
  pro = false,
  variant = "default",
}: {
  icon: LucideIcon;
  label: string;
  onClick?: () => void;
  /** External link rows (docs) — rendered as an anchor. */
  href?: string;
  pro?: boolean;
  variant?: "default" | "danger";
}) {
  const className = `w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-left transition-colors cursor-pointer ${
    variant === "danger"
      ? "text-muted-foreground hover:bg-destructive/10 hover:text-destructive active:bg-destructive/15"
      : "text-muted-foreground hover:bg-muted hover:text-foreground active:bg-muted/80"
  }`;

  const inner = (
    <>
      <Icon size={18} strokeWidth={1.75} className="flex-shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      {pro && (
        <span className="flex-shrink-0 rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-brand bg-brand/10">
          Pro
        </span>
      )}
    </>
  );

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className}>
      {inner}
    </button>
  );
}
